import { Injectable } from '@angular/core';
import { Observable, from, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocumentProxy, PDFPageProxy, PageViewport } from 'pdfjs-dist';

// Configure PDF.js worker
const PDFJS_WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;

export interface PdfMetadata {
  pageCount: number;
  title?: string;
  author?: string;
  subject?: string;
  keywords?: string;
  creator?: string;
  producer?: string;
  creationDate?: Date;
  modificationDate?: Date;
}

export interface PdfPageInfo {
  pageNumber: number;
  width: number;
  height: number;
  rotation: number;
}

export interface RenderOptions {
  canvasContext: CanvasRenderingContext2D;
  viewport: PageViewport;
}

@Injectable({
  providedIn: 'root'
})
export class PdfViewerService {
  private currentDocument: PDFDocumentProxy | null = null;
  private currentPage: PDFPageProxy | null = null;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor() { }

  /**
   * Load a PDF document from a URL or File
   * @param source URL string or File object
   * @returns Observable of PDFDocumentProxy
   */
  loadDocument(source: string | File | Uint8Array): Observable<PDFDocumentProxy> {
    this.loadingSubject.next(true);

    let loadingTask;

    if (typeof source === 'string') {
      // Load from URL
      loadingTask = pdfjsLib.getDocument(source);
    } else if (source instanceof File) {
      // Load from File
      return new Observable(observer => {
        const fileReader = new FileReader();
        fileReader.onload = (e) => {
          const typedArray = new Uint8Array(e.target?.result as ArrayBuffer);
          loadingTask = pdfjsLib.getDocument(typedArray);

          from(loadingTask.promise).pipe(
            catchError(error => {
              this.loadingSubject.next(false);
              return throwError(() => new Error(`Failed to load PDF: ${error.message}`));
            })
          ).subscribe({
            next: (pdf) => {
              this.currentDocument = pdf;
              this.loadingSubject.next(false);
              observer.next(pdf);
              observer.complete();
            },
            error: (err) => {
              observer.error(err);
            }
          });
        };
        fileReader.onerror = () => {
          this.loadingSubject.next(false);
          observer.error(new Error('Failed to read file'));
        };
        fileReader.readAsArrayBuffer(source);
      });
    } else {
      // Load from Uint8Array
      loadingTask = pdfjsLib.getDocument(source);
    }

    return from(loadingTask.promise).pipe(
      map((pdf: PDFDocumentProxy) => {
        this.currentDocument = pdf;
        this.loadingSubject.next(false);
        return pdf;
      }),
      catchError(error => {
        this.loadingSubject.next(false);
        return throwError(() => new Error(`Failed to load PDF: ${error.message}`));
      })
    );
  }

  /**
   * Get a specific page from the loaded document
   * @param pageNumber Page number (1-indexed)
   * @returns Observable of PDFPageProxy
   */
  getPage(pageNumber: number): Observable<PDFPageProxy> {
    if (!this.currentDocument) {
      return throwError(() => new Error('No document loaded'));
    }

    if (pageNumber < 1 || pageNumber > this.currentDocument.numPages) {
      return throwError(() => new Error(`Invalid page number: ${pageNumber}`));
    }

    return from(this.currentDocument.getPage(pageNumber)).pipe(
      map((page: PDFPageProxy) => {
        this.currentPage = page;
        return page;
      }),
      catchError(error => {
        return throwError(() => new Error(`Failed to get page: ${error.message}`));
      })
    );
  }

  /**
   * Render a PDF page on a canvas
   * @param page PDFPageProxy to render
   * @param canvas Canvas element to render on
   * @param scale Scale factor for rendering
   * @returns Observable of void
   */
  renderPage(page: PDFPageProxy, canvas: HTMLCanvasElement, scale: number = 1.0): Observable<void> {
    const viewport = page.getViewport({ scale });
    const context = canvas.getContext('2d');

    if (!context) {
      return throwError(() => new Error('Failed to get canvas context'));
    }

    // Set canvas dimensions
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    // Clear canvas before rendering
    context.clearRect(0, 0, canvas.width, canvas.height);

    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };

    const renderTask = page.render(renderContext);

    return from(renderTask.promise).pipe(
      map(() => {
        return undefined;
      }),
      catchError(error => {
        // If error is due to render cancellation, ignore it
        if (error.message && error.message.includes('Rendering cancelled')) {
          return of(undefined);
        }
        return throwError(() => new Error(`Failed to render page: ${error.message}`));
      })
    );
  }

  /**
   * Get metadata from the loaded document
   * @returns Observable of PdfMetadata
   */
  getMetadata(): Observable<PdfMetadata> {
    if (!this.currentDocument) {
      return throwError(() => new Error('No document loaded'));
    }

    return from(this.currentDocument.getMetadata()).pipe(
      map((metadata: any) => {
        const pdfMetadata: PdfMetadata = {
          pageCount: this.currentDocument!.numPages,
          title: metadata.info?.Title,
          author: metadata.info?.Author,
          subject: metadata.info?.Subject,
          keywords: metadata.info?.Keywords,
          creator: metadata.info?.Creator,
          producer: metadata.info?.Producer,
          creationDate: this.parsePdfDate(metadata.info?.CreationDate),
          modificationDate: this.parsePdfDate(metadata.info?.ModDate)
        };
        return pdfMetadata;
      }),
      catchError(error => {
        return throwError(() => new Error(`Failed to get metadata: ${error.message}`));
      })
    );
  }

  /**
   * Get page information (dimensions, rotation)
   * @param pageNumber Page number (1-indexed)
   * @returns Observable of PdfPageInfo
   */
  getPageInfo(pageNumber: number): Observable<PdfPageInfo> {
    return this.getPage(pageNumber).pipe(
      map((page: PDFPageProxy) => {
        const viewport = page.getViewport({ scale: 1.0 });
        return {
          pageNumber: pageNumber,
          width: viewport.width,
          height: viewport.height,
          rotation: page.rotate
        };
      })
    );
  }

  /**
   * Get the current loaded document
   * @returns PDFDocumentProxy or null
   */
  getCurrentDocument(): PDFDocumentProxy | null {
    return this.currentDocument;
  }

  /**
   * Get the current page
   * @returns PDFPageProxy or null
   */
  getCurrentPage(): PDFPageProxy | null {
    return this.currentPage;
  }

  /**
   * Get page count of loaded document
   * @returns number of pages or 0 if no document loaded
   */
  getPageCount(): number {
    return this.currentDocument?.numPages || 0;
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    if (this.currentDocument) {
      this.currentDocument.destroy();
      this.currentDocument = null;
    }
    this.currentPage = null;
    this.loadingSubject.next(false);
  }

  /**
   * Parse PDF date format (D:YYYYMMDDHHmmss+TZ)
   * @param dateString PDF date string
   * @returns Date object or undefined
   */
  private parsePdfDate(dateString?: string): Date | undefined {
    if (!dateString) {
      return undefined;
    }

    try {
      // PDF date format: D:YYYYMMDDHHmmss+HH'mm' or D:YYYYMMDDHHmmss-HH'mm'
      const match = dateString.match(/D:(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})([+-]\d{2})'(\d{2})'/);

      if (match) {
        const [, year, month, day, hour, minute, second, tzHour, tzMinute] = match;
        const date = new Date(
          parseInt(year),
          parseInt(month) - 1, // months are 0-indexed
          parseInt(day),
          parseInt(hour),
          parseInt(minute),
          parseInt(second)
        );
        return date;
      }

      // Fallback to simpler format
      const simpleMatch = dateString.match(/D:(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
      if (simpleMatch) {
        const [, year, month, day, hour, minute, second] = simpleMatch;
        return new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour),
          parseInt(minute),
          parseInt(second)
        );
      }

      return undefined;
    } catch (error) {
      return undefined;
    }
  }
}
